import React, { useState, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link, useLocation } from 'react-router-dom';
import { Tag, Typography, Button, Divider, theme, ConfigProvider } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { ESensorMode } from '@/types';
import {
  normalizeModel,
  HEATIC2_PLUS_NORMALIZED,
  pluralize,
  computeHealthCounts,
  buildSensorBadgeConfigs,
  VERTICAL_DIVIDER_STYLE,
  SENSOR_TAG_STYLE,
} from '../shared/sensorUtils';
import { useGetSensors3pGateways3p } from '@/components/studio/ScopingEditor/useGetSensors3pGateways3p';
import WarningIcon from '@/components/Icons/Warning';
import CrosshairIcon from '@/components/Icons/Crosshair';
import DoorIcon from '@/components/Icons/Door';
import ExpandedCapabilities from '../shared/ExpandedCapabilities';
import { SENSOR_BADGE_ICONS, ICON_STYLE } from '../shared/sensorBadgeIcons';
import { useLinkButtonTheme } from '../shared/useLinkButtonTheme';

// Self-contained query: fetches sensor layout + health + PIR config in one go.
// Apollo deduplicates overlapping fields with the parent DashboardFloorQuery.
const FLOOR_SETUP_QUERY = gql`
  query FloorSetupData($floorId: String!) {
    floors(ids: [$floorId]) {
      data {
        capacity {
          max
        }
        sensors {
          id: sensor_id
          mode
          isEntrance: is_entrance
          isOnline: is_online
          isSleepTime: is_sleep_time
          connectionHealth: connection_health {
            status
          }
          room {
            id: room_id
          }
        }
        rooms {
          id: room_id
          sensors {
            mode
            model
            config {
              use_pir
            }
          }
        }
      }
    }
  }
`;

interface FloorSetupCardProps {
  spaceId: string;
}

const SUPPORT_ARTICLE_URL =
  'https://support.butlr.io/hc/en-us/articles/44879874832013-Why-Do-Different-Floors-Have-Different-Dashboards';

const FloorSetupCard: React.FC<FloorSetupCardProps> = ({ spaceId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { search } = useLocation();
  const { token } = theme.useToken();
  const linkButtonTheme = useLinkButtonTheme();

  const { data, error } = useQuery(FLOOR_SETUP_QUERY, {
    variables: { floorId: spaceId },
    skip: !spaceId,
  });

  const { sensors3p } = useGetSensors3pGateways3p({ spaceId });

  const floorData = data?.floors?.data?.[0];
  const floorCapacity: number = floorData?.capacity?.max ?? 0;
  const sensors = useMemo(
    (): Array<{
      id: string;
      mode: string;
      isEntrance: boolean;
      isOnline: boolean;
      isSleepTime: boolean;
      connectionHealth: { status: string };
      room?: { id: string } | null;
    }> => floorData?.sensors ?? [],
    [floorData],
  );

  const healthRooms = useMemo(
    (): Array<{
      id: string;
      sensors: Array<{ mode: string; model: string; config?: { use_pir: boolean } | null }>;
    }> => floorData?.rooms ?? [],
    [floorData],
  );

  const trafficSensors = useMemo(
    () => sensors.filter((s) => s.mode === ESensorMode.TRAFFIC),
    [sensors],
  );
  const presenceSensors = useMemo(
    () => sensors.filter((s) => s.mode === ESensorMode.PRESENCE),
    [sensors],
  );

  const motionSensors = useMemo(
    () => sensors3p.filter((s) => s.device_type === 'motion_sensor'),
    [sensors3p],
  );
  const deskSensors = useMemo(
    () => sensors3p.filter((s) => s.device_type === 'desk_sensor'),
    [sensors3p],
  );

  const hasEntranceMonitoring = trafficSensors.some((s) => s.isEntrance);
  const roomLevelTrafficCount = useMemo(
    () => trafficSensors.filter((s) => s.room?.id).length,
    [trafficSensors],
  );
  const hasTraffic = trafficSensors.length > 0;
  const hasPresence = presenceSensors.length > 0;
  const pirRoomCount = useMemo(() => {
    if (healthRooms.length === 0) return 0;
    return healthRooms.filter((r) => {
      const roomSensors = r.sensors ?? [];
      const pirEnabled = roomSensors.some((s) => s.config?.use_pir);
      const hasTrafficSensor = roomSensors.some((s) => s.mode === ESensorMode.TRAFFIC);
      const hasHeatic2PlusPresence = roomSensors.some(
        (s) =>
          s.mode === ESensorMode.PRESENCE && normalizeModel(s.model) === HEATIC2_PLUS_NORMALIZED,
      );
      return pirEnabled && hasTrafficSensor && hasHeatic2PlusPresence;
    }).length;
  }, [healthRooms]);
  const hasPir = pirRoomCount > 0;

  const { offlineCount, warningCount } = useMemo(() => computeHealthCounts(sensors), [sensors]);

  const issueCount = offlineCount + warningCount;

  const sensorTypeBadges = useMemo(
    () =>
      buildSensorBadgeConfigs({
        traffic: trafficSensors.length,
        presence: presenceSensors.length,
        motion: motionSensors.length,
        desk: deskSensors.length,
      }),
    [trafficSensors.length, presenceSensors.length, motionSensors.length, deskSensors.length],
  );

  // Data capabilities: what data the floor can produce
  const dataCapabilities = useMemo(() => {
    const items: { title: string; description: string }[] = [];
    if (hasTraffic) {
      const suffix = hasEntranceMonitoring
        ? ' · Main entrance tracked'
        : roomLevelTrafficCount > 0
        ? ` · ${roomLevelTrafficCount} room-level`
        : '';
      items.push({
        title: 'Entry/Exit Traffic Counts',
        description: `${pluralize(trafficSensors.length, 'traffic sensor')} deployed${suffix}`,
      });
    }
    if (hasPresence) {
      items.push({
        title: 'Open Area Occupancy Estimates',
        description: `${pluralize(presenceSensors.length, 'presence sensor')} deployed`,
      });
    }
    if (hasPir) {
      items.push({
        title: 'Auto-Zeroing Calibration',
        description: `${pluralize(pirRoomCount, 'room')} with PIR auto-zeroing active`,
      });
    }
    if (deskSensors.length > 0) {
      items.push({
        title: 'Desk Occupancy',
        description: `${pluralize(deskSensors.length, 'desk sensor')} deployed`,
      });
    }
    return items;
  }, [
    hasTraffic,
    hasPresence,
    hasPir,
    pirRoomCount,
    hasEntranceMonitoring,
    trafficSensors.length,
    presenceSensors.length,
    deskSensors.length,
    roomLevelTrafficCount,
  ]);

  // Features: higher-level processing on top of data capabilities
  const enabledFeatures = useMemo(() => {
    const items: { title: string; description: string; icon: React.ReactNode }[] = [];
    if (hasPir) {
      items.push({
        title: 'PIR Zeroing',
        description: `${pluralize(pirRoomCount, 'room')} with auto-calibration`,
        icon: <CrosshairIcon style={ICON_STYLE} />,
      });
    }
    if (hasEntranceMonitoring) {
      items.push({
        title: 'Main Entrance Monitoring',
        description: 'Floor-level IN/OUT traffic tracking enabled',
        icon: <DoorIcon style={ICON_STYLE} />,
      });
    }
    return items;
  }, [hasPir, hasEntranceMonitoring, pirRoomCount]);

  // Don't render anything until data is loaded (but still show error if query failed)
  if (!floorData && !error) return null;

  return (
    <div
      style={{
        paddingLeft: token.paddingLG,
        paddingRight: token.paddingLG,
        paddingTop: token.paddingXXS,
        paddingBottom: token.paddingXXS,
        backgroundColor: token.colorBgContainer,
      }}
    >
      {/* Sensor badges row + offline badge + What's Enabled */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label="Toggle sensor details"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: token.marginXS,
          flexWrap: 'wrap',
          cursor: 'pointer',
        }}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: token.marginXS, flexWrap: 'wrap' }}
        >
          <Typography.Text type="secondary">Installed:</Typography.Text>
          {sensorTypeBadges.map((badge) => (
            <span
              key={badge.label}
              style={{ display: 'inline-flex', alignItems: 'center', gap: token.marginXXS }}
            >
              <Typography.Text>{badge.count} x</Typography.Text>
              <Tag color={badge.color} bordered={false} style={SENSOR_TAG_STYLE}>
                {SENSOR_BADGE_ICONS[badge.label]}
                {badge.label}
              </Tag>
            </span>
          ))}
          {/* Capacity */}
          {floorCapacity > 0 && (
            <>
              <Divider type="vertical" style={VERTICAL_DIVIDER_STYLE} />
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.marginXXS }}>
                <Typography.Text type="secondary">Capacity:</Typography.Text>
                <Typography.Text>{floorCapacity}</Typography.Text>
              </span>
            </>
          )}

          {/* Divider between sensors/capacity and offline alert */}
          {issueCount > 0 && <Divider type="vertical" style={VERTICAL_DIVIDER_STYLE} />}
          {/* Offline badge */}
          {issueCount > 0 && (
            <Link
              to={`/devices${search}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: token.marginXXS,
                padding: `${token.paddingXXS}px ${token.paddingXS}px`,
                borderRadius: token.borderRadiusLG,
                fontSize: token.fontSizeSM,
                fontWeight: 500,
                backgroundColor: token.colorErrorBg,
                color: token.colorError,
                border: `1px solid ${token.colorErrorBorder}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <WarningIcon style={ICON_STYLE} />
              {offlineCount > 0 && `${pluralize(offlineCount, 'Sensor')} Offline`}
              {offlineCount > 0 && warningCount > 0 && ', '}
              {warningCount > 0 && `${warningCount} Warning`}
            </Link>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: token.marginMD }}>
          {/* What's Enabled toggle */}
          <ConfigProvider theme={linkButtonTheme}>
            <Button
              type="link"
              size="small"
              icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
              iconPosition="end"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              What&apos;s Enabled
            </Button>
          </ConfigProvider>
        </div>
      </div>

      {/* Expanded: enabled data capabilities + features */}
      {isExpanded && error && (
        <div style={{ marginTop: token.marginSM }}>
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            Unable to load sensor health data.
          </Typography.Text>
        </div>
      )}
      {isExpanded && !error && (
        <ExpandedCapabilities
          dataCapabilities={dataCapabilities}
          enabledFeatures={enabledFeatures}
          emptyMessage="No data capabilities enabled on this floor."
          headerExtra={
            <Typography.Link
              href={SUPPORT_ARTICLE_URL}
              target="_blank"
              type="secondary"
              style={{ fontSize: token.fontSizeSM }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              Why is this different across floors?
            </Typography.Link>
          }
        />
      )}
    </div>
  );
};

export default FloorSetupCard;
